// --- FILE: src/hooks/useShiftManager.js (FINAL - CORREGIDO Y USANDO UTILS) ---
import { useState, useCallback } from 'react';
import { db } from '../services/firebase';
import {
  doc, // Importar doc
  updateDoc,
  addDoc,
  collection,
  Timestamp,
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
// Importar la función de zona horaria desde utils
import { puertoRicoTimeToUtc, utcToPuertoRicoTime } from '../utils/formatters'; //

// Zona horaria offset para Puerto Rico (UTC-4) - ya no se necesita aquí si se importa
// const PR_TIMEZONE_OFFSET = -4; 


/**
 * Hook para la gestión de turnos por parte de un administrador.
 * Proporciona funciones para crear y actualizar turnos manualmente.
 */
export const useShiftManager = () => {
  const { user } = useAuth(); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  /**
   * Calcula la duración en minutos y las ganancias de un turno.
   * Las fechas de entrada ya deben estar en la zona horaria correcta o ser UTC.
   * @param {Date} startTime - Objeto Date (UTC, pero representando la hora de PR).
   * @param {Date} endTime - Objeto Date (UTC, pero representando la hora de PR).
   * @param {number} hourlyRate - Tarifa por hora del empleado.
   * @returns {{durationMinutes: number, earnings: number}}
   */
  const calculateShiftDetails = (startTime, endTime, hourlyRate) => {
    if (!startTime || !endTime || endTime.getTime() < startTime.getTime()) {
      return { durationMinutes: 0, earnings: 0 };
    }
    const durationMillis = endTime.getTime() - startTime.getTime();
    const durationMinutes = Math.round(durationMillis / 60000); // Redondear a minutos completos
    
    // Asegurarse de que hourlyRate es un número válido antes de calcular
    const validHourlyRate = typeof hourlyRate === 'number' && !isNaN(hourlyRate) ? hourlyRate : 10.50; 
    const earnings = (durationMinutes / 60) * validHourlyRate;

    return {
      durationMinutes,
      earnings: parseFloat(earnings.toFixed(2)), // Redondear ganancias a 2 decimales
    };
  };

  /**
   * Crea un nuevo turno en Firestore.
   * @param {object} shiftData - Datos del turno a crear.
   * @param {string} shiftData.employeeId - ID del empleado.
   * @param {string} shiftData.employeeName - Nombre del empleado.
   * @param {Date} shiftData.startTime - Fecha y hora de inicio (objeto Date local del navegador).
   * @param {Date} shiftData.endTime - Fecha y hora de fin (objeto Date local del navegador).
   * @param {number} shiftData.hourlyRate - Tarifa por hora del empleado.
   */
  const createShift = useCallback(async (shiftData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    const { employeeId, employeeName, startTime, endTime, hourlyRate } = shiftData;

    if (!employeeId || !startTime || !endTime) {
      setError('Faltan datos requeridos para crear el turno.');
      setLoading(false);
      return false;
    }

    if (endTime.getTime() < startTime.getTime()) { 
      setError('La hora de fin no puede ser anterior a la hora de inicio.');
      setLoading(false);
      return false;
    }

    try {
      // 1. Interpretar las fechas de entrada como si fueran hora de Puerto Rico
      // y convertirlas a UTC para almacenar en Firestore
      const firestoreStartTime = puertoRicoTimeToUtc(startTime); //
      const firestoreEndTime = puertoRicoTimeToUtc(endTime); //
      
      const { durationMinutes, earnings } = calculateShiftDetails(firestoreStartTime, firestoreEndTime, hourlyRate);

      const newShift = {
        employeeId,
        employeeName,
        startTime: Timestamp.fromDate(firestoreStartTime), 
        endTime: Timestamp.fromDate(firestoreEndTime), 
        durationMinutes,
        earnings,
        status: 'completed', 
        createdAt: Timestamp.now(), 
        updatedAt: Timestamp.now(), 
        editedBy: user?.uid || 'admin_manual', 
      };

      await addDoc(collection(db, 'shifts'), newShift);
      setSuccess(true);
      setLoading(false);
      return true;
    } catch (err) {
      console.error("Error al crear el turno:", err);
      setError('No se pudo crear el turno en la base de datos.');
      setLoading(false);
      return false;
    }
  }, [user]);

  /**
   * Actualiza un turno existente en Firestore.
   * @param {string} shiftId - ID del documento del turno a actualizar.
   * @param {object} updateData - Datos a actualizar.
   * @param {Date} updateData.startTime - Nueva fecha y hora de inicio (objeto Date local del navegador).
   * @param {Date} updateData.endTime - Nueva fecha y hora de fin (objeto Date local del navegador).
   * @param {number} updateData.hourlyRate - Tarifa por hora del empleado.
   */
  const updateShift = useCallback(async (shiftId, updateData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    const { startTime, endTime, hourlyRate } = updateData;

    if (!shiftId || !startTime || !endTime) {
      setError('Faltan datos requeridos para actualizar el turno.');
      setLoading(false);
      return false;
    }

     if (endTime.getTime() < startTime.getTime()) { 
      setError('La hora de fin no puede ser anterior a la hora de inicio.');
      setLoading(false);
      return false;
    }

    try {
      // 1. Interpretar las fechas de entrada como si fueran hora de Puerto Rico
      // y convertirlas a UTC para almacenar en Firestore
      const firestoreStartTime = puertoRicoTimeToUtc(startTime); //
      const firestoreEndTime = puertoRicoTimeToUtc(endTime); //

      const { durationMinutes, earnings } = calculateShiftDetails(firestoreStartTime, firestoreEndTime, hourlyRate);
      const shiftRef = doc(db, 'shifts', shiftId); //

      await updateDoc(shiftRef, {
        startTime: Timestamp.fromDate(firestoreStartTime), 
        endTime: Timestamp.fromDate(firestoreEndTime), 
        durationMinutes,
        earnings,
        updatedAt: Timestamp.now(),
        editedBy: user?.uid || 'admin_manual',
      });

      setSuccess(true);
      setLoading(false);
      return true;
    } catch (err) {
      console.error("Error al actualizar el turno:", err);
      setError('No se pudo actualizar el turno en la base de datos.');
      setLoading(false);
      return false;
    }
  }, [user]);

  // Función para resetear el estado de éxito y poder cerrar un modal, por ejemplo.
  const resetStatus = () => {
    setSuccess(false);
    setError(null);
  }

  return {
    createShift,
    updateShift,
    loading,
    error,
    success,
    resetStatus,
    // Ya no es necesario exportar estas funciones desde aquí, ya que están en utils/formatters.js
    // utcToPuertoRicoTime,
    // puertoRicoTimeToUtc
  };
};